import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import CircuitField from "@/components/CircuitField";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

export default function Work() {
    const { featuredProject, clientProjects, conceptProjects, industries } = useSiteContent();
    const heroImgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroImgRef, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    return (
        <div data-testid="work-page">
            <SEO
                title="Our Work | Website Design Portfolio — Camarca Digital Designs"
                description="See real client websites built by Camarca Digital Designs — ASAP & CO., Briars Unlimited, GAP Outdoors, Kinetic Facilities, Seraphina Collective, Skinplicity Wellness — plus design concepts for auto, HVAC, lawn care, plumbing, pressure washing and salon businesses."
                path="/work"
            />

            <div className="relative overflow-hidden">
                <CircuitField className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
                <header className="relative mx-auto max-w-7xl px-6 pb-16 pt-40 lg:px-10">
                    <Reveal>
                        <SectionTag>Selected Work</SectionTag>
                        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            Proof, not{" "}
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">promises.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl leading-relaxed text-zinc-400 md:text-lg">
                            Seven live client websites you can visit right now — plus design concepts showing the look your business could have.
                        </p>
                    </Reveal>
                </header>
            </div>

            {/* FEATURED PROJECT */}
            <section data-testid="featured-project-section" className="mx-auto max-w-7xl px-6 lg:px-10">
                <Reveal>
                    <div ref={heroImgRef} className="clip-frame relative overflow-hidden border border-white/10">
                        <motion.img
                            style={{ y: imgY }}
                            src={featuredProject.images[0].src}
                            alt={featuredProject.images[0].alt}
                            className="aspect-[16/9] w-full scale-110 object-cover"
                            loading="lazy"
                        />
                        <div className="absolute left-4 top-4 bg-black/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-neon backdrop-blur-sm">
                            Featured client build
                        </div>
                    </div>
                </Reveal>

                <div className="mt-12 grid gap-12 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">{featuredProject.category}</p>
                        <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">{featuredProject.name}</h2>
                        <p className="mt-6 max-w-2xl leading-relaxed text-zinc-400">{featuredProject.description}</p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {featuredProject.deliverables.map((d) => (
                                <span key={d} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-zinc-400">
                                    {d}
                                </span>
                            ))}
                        </div>
                        <div className="mt-8 flex flex-wrap items-center gap-6">
                            <Link
                                to="/contact"
                                data-testid="featured-project-cta"
                                className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-neon"
                            >
                                Request something similar
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                            <Link
                                to="/work/asap-co"
                                data-testid="featured-case-study-link"
                                className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 hover:text-neon"
                            >
                                Read the case study
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                        </div>
                    </Reveal>

                    <div className="grid gap-6 lg:col-span-5">
                        {featuredProject.images.slice(1).map((img, i) => (
                            <Reveal key={img.src} delay={0.1 * (i + 1)}>
                                <figure className="group overflow-hidden clip-frame-tl border border-white/10">
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        loading="lazy"
                                        className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </figure>
                                <p className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-600">{img.caption}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLIENT WEBSITES */}
            <section data-testid="client-sites-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <SectionTag>Client Websites</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
                            Live sites, live businesses.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-zinc-500">
                        Every one of these is a real business running on a Camarca build. Click through and look around.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-6 md:grid-cols-2">
                    {clientProjects.map((p, i) => (
                        <Reveal key={p.name} delay={(i % 2) * 0.1} className={i === 0 ? "md:col-span-2" : ""}>
                            <Link
                                to={`/work/${p.slug}`}
                                data-testid={`client-site-card-${i}`}
                                className="group relative block overflow-hidden border border-white/10 clip-frame transition-[border-color] duration-500 hover:border-neon/50"
                            >
                                <img
                                    src={p.image}
                                    alt={p.alt}
                                    loading="lazy"
                                    className={`w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "aspect-[21/9]" : "aspect-[16/9]"}`}
                                />
                                <span className="absolute left-4 top-4 bg-black/70 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-neon backdrop-blur-sm">
                                    Live Site
                                </span>
                                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black via-black/70 to-transparent p-6 md:p-8">
                                    <div className="flex w-full items-end justify-between gap-4">
                                        <div>
                                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neon">
                                                {p.category}{p.location ? ` — ${p.location}` : ""}
                                            </p>
                                            <h3 className="mt-2 font-display text-xl font-bold md:text-2xl">{p.name}</h3>
                                            <p className="mt-2 max-w-md text-sm text-zinc-400 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                                {p.blurb}
                                            </p>
                                        </div>
                                        <span className="flex shrink-0 items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white">
                                            Case Study
                                            <ArrowUpRight className="h-4 w-4 text-neon transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CONCEPTS */}
            <section data-testid="concepts-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <SectionTag>Design Concepts</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
                            Your industry. Your look.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-zinc-500">
                        Sample concepts built to show the type of design Camarca can create for businesses of any size, in any industry.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {conceptProjects.map((p, i) => (
                        <Reveal key={p.name} delay={(i % 3) * 0.08}>
                            <div data-testid={`concept-card-${i}`} className="group relative overflow-hidden border border-white/10 clip-frame">
                                <img
                                    src={p.image}
                                    alt={p.alt}
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <span className="absolute left-4 top-4 bg-black/70 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 backdrop-blur-sm">
                                    Concept
                                </span>
                                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/90 via-black/20 to-transparent p-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                    <div>
                                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neon">{p.category}</p>
                                        <h3 className="mt-2 font-display text-lg font-bold">{p.name}</h3>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* INDUSTRIES */}
            <section data-testid="industries-strip" className="border-t border-white/10">
                <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
                    <Reveal>
                        <SectionTag>Industries</SectionTag>
                        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
                            Built for your industry.
                        </h2>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-500">
                            A few industries I build for — but whatever yours is, the answer is yes.
                        </p>
                    </Reveal>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {industries.map((ind, i) => (
                            <Reveal key={ind.slug} delay={i * 0.06}>
                                <Link
                                    to={`/industries/${ind.slug}`}
                                    data-testid={`industry-card-${ind.slug}`}
                                    className="group flex h-full flex-col justify-between border border-white/10 bg-surface p-6 transition-[border-color,box-shadow] duration-500 hover:border-neon/50 hover:shadow-[0_0_25px_rgba(0,240,255,0.08)]"
                                >
                                    <div>
                                        <span className="font-mono text-xs text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                                        <h3 className="mt-4 font-display text-base font-bold leading-snug">{ind.label}</h3>
                                    </div>
                                    <span className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 group-hover:text-neon">
                                        Web design
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-white/10 bg-surface/40">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-20 lg:px-10">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold md:text-3xl">Want to be the next build?</h2>
                        <p className="mt-3 max-w-lg text-zinc-400">Tell me about your business and let's design something worth showing off.</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="work-bottom-cta"
                            className="group flex items-center gap-3 bg-neon px-8 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                        >
                            Start a Project
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
